/**
 * Always hints — checks/hints that are guaranteed to be hinted in a given seed.
 * Each entry has a `kind` (regular | dual | entrance) used for visual badging
 * and so presets can target the right hint type.
 */
import Cow from '@/core/assets/oot/classic/cow.png';
import Scrub from '@/core/assets/oot/classic/business_scrub.png';
import Frog from '@/core/assets/oot/gshd/frogs_32x32.png';
import OcarinaOfTime from '@/core/assets/oot/gshd/ocarina-of-time_32x32.png';
import Nocturne from '@/core/assets/oot/song/nocturne.png';
import SkullMask from '@/core/assets/oot/gshd/skull-mask_32x32.png';
import GoronMask from '@/core/assets/oot/gshd/biggoron_32x32.png';
import Token50 from '@/core/assets/oot/gshd/50-gold-skulltula_32x32.png';
import Token40 from '@/core/assets/oot/gshd/40-gold-skulltula_32x32.png';
import Token30 from '@/core/assets/oot/gshd/30-gold-skulltula_32x32.png';
import Token20 from '@/core/assets/oot/classic/20_token.png';
import MaskOfTruth from '@/core/assets/oot/gshd/mask-of-truth_32x32.png';
import ForestMedallion from '@/core/assets/oot/gshd/green-medallion_32x32.png';
import ShadowMedallion from '@/core/assets/oot/gshd/purple-medallion_32x32.png';

export type AlwaysCondition = 'cowsanity' | 'scrubsanity';
export type AlwaysKind = 'regular' | 'dual' | 'entrance';

export interface AlwaysHintData {
  /** Stable identifier (also used as state key). */
  name: string;
  /** Sub-category — drives the badge color in the UI. */
  kind: AlwaysKind;
  /** Optional image — replaces the text label when present. */
  img?: string;
  /** Optional second image (for dual hints with two icons). */
  img2?: string;
  /** Part of the default always pool. */
  always?: boolean;
  /** Active when multiworld distribution is on. */
  multiworld?: boolean;
  /** Activated by a sanity toggle. */
  alwaysCondition?: AlwaysCondition;
}

export const ALWAYS_HINTS: AlwaysHintData[] = [
  // Regular always
  { name: 'ZR Frogs Ocarina Game', kind: 'regular', img: Frog, always: true, multiworld: true },
  { name: 'Song from Ocarina of Time', kind: 'regular', img: OcarinaOfTime, always: true, multiworld: true },
  { name: 'Sheik in Kakariko', kind: 'regular', img: Nocturne, always: true, multiworld: true },
  { name: 'Deku Theater Skull Mask', kind: 'regular', img: SkullMask, always: true },
  { name: 'DMT Biggoron', kind: 'regular', img: GoronMask, always: true },
  { name: 'Kak 50 Gold Skulltula Reward', kind: 'regular', img: Token50, always: true },
  { name: 'Kak 40 Gold Skulltula Reward', kind: 'regular', img: Token40, always: true },
  { name: 'Kak 30 Gold Skulltula Reward', kind: 'regular', img: Token30, multiworld: true },
  { name: 'Kak 20 Gold Skulltula Reward', kind: 'regular', img: Token20, multiworld: true },
  { name: 'KF Links House Cow', kind: 'regular', img: Cow, alwaysCondition: 'cowsanity' },
  { name: 'DMC Deku Scrub', kind: 'regular', img: Scrub, alwaysCondition: 'scrubsanity' },

  // Dual always
  { name: 'Deku Theater Rewards', kind: 'dual', img: SkullMask, img2: MaskOfTruth },

  // Entrance always (boss door entrances under entrance shuffle)
  { name: 'Forest Boss Door Entrance', kind: 'entrance', img: ForestMedallion },
  { name: 'Shadow Boss Door Entrance', kind: 'entrance', img: ShadowMedallion },
];

interface AlwaysHintSettings {
  isMultiworld: boolean;
  isCowsanity: boolean;
  isScrubsanity: boolean;
}

/**
 * Compute the active always-hints list for a session based on settings.
 * Kept for compatibility / future "preset from settings" feature; the page
 * itself reads from `state.alwaysHintNames`.
 */
export function getActiveAlwaysHints(s: AlwaysHintSettings): AlwaysHintData[] {
  const base = s.isMultiworld
    ? ALWAYS_HINTS.filter((h) => h.multiworld && !h.alwaysCondition)
    : ALWAYS_HINTS.filter((h) => h.always && !h.alwaysCondition);

  const conditionals: AlwaysHintData[] = [];
  if (s.isCowsanity) {
    conditionals.push(...ALWAYS_HINTS.filter((h) => h.alwaysCondition === 'cowsanity'));
  }
  if (s.isScrubsanity) {
    conditionals.push(...ALWAYS_HINTS.filter((h) => h.alwaysCondition === 'scrubsanity'));
  }

  return [...base, ...conditionals];
}

export const ALWAYS_KIND_BADGE: Record<AlwaysKind, { label: string; color: string }> = {
  regular: { label: '', color: '' },
  dual: { label: 'D', color: 'text-purple-300 bg-purple-900/60 border border-purple-500/40' },
  entrance: { label: 'E', color: 'text-sky-300 bg-sky-900/60 border border-sky-500/40' },
};
